export {
    fetchRequestSend,
    detectOS,
    smoothScrollToAnchor,
    showPassword,
    setCookieHelper,
    getCookieHelper,
    // Need for CoursePassingProcessClass.js
    setCookie,
    getCookie
};

/**
 * Performs an HTTP request to a specified URL.
 *
 * @param {string} url - The URL to which the request is sent.
 * @param {string} method - The HTTP method to be used (e.g., 'GET', 'POST').
 * @param {Object} [body=null] - The payload for the request, applicable for methods like 'POST'.
 * @returns {Promise<Object>} A Promise that resolves to the parsed JSON response.
 */
const fetchRequestSend = async (url, method, body = null, isFile = false) => {
    csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
    // Initialize options for the fetch request
    const headers = {
        'X-CSRF-Token': csrfToken,
    };

    const options = {
        method: method,
        headers: headers
    };

    // Add the body to the request if it's provided
    if (body) {
        if (body instanceof FormData) {
            options.body = body;
        } else {
            options.headers['Content-Type'] = 'application/json';
            options.headers['Accept'] = 'application/json';
            options.body = typeof body !== 'string' ? JSON.stringify(body) : body;
        }
    }
    // Perform the fetch request
    const response = await fetch(url, options);

    // Parse and return the JSON response
    return response.json();
}
/**
 * Detects the user's operating system and adds a corresponding class to the body element.
 * This function checks the user's navigator.userAgent to identify the OS and then
 * adds a class to the body element of the HTML document.
 * Supported OS classes: 'windows', 'macos', 'linux', 'ios', 'android', 'unknown-os'.
 */
function detectOS() {
    const userAgent = window.navigator.userAgent;
    const platform = window.navigator.platform;
    let osClass = 'unknown-os';

    if (/Windows/.test(platform)) {
        osClass = 'windows';
    } else if (/Macintosh/.test(platform) || /MacIntel/.test(platform)) {
        osClass = 'macos';
    } else if (/Linux/.test(platform)) {
        osClass = 'linux';
    } else if (/iPhone/.test(userAgent) || /iPad/.test(userAgent) || /iPod/.test(userAgent)) {
        osClass = 'ios';
    } else if (/Android/.test(userAgent)) {
        osClass = 'android';
    }

    document.body.classList.add(osClass);
}

/**
 * Scrolls smoothly to the anchor element.
 * This function adds a click event listener to all anchor elements with the href attribute
 */
const smoothScrollToAnchor = () => {
    const links = document.querySelectorAll('a[href^="#"]');
    for (let link of links) {
        if (link.getAttribute('href').length > 1) {
            link.addEventListener('click', (e) => {
                e.preventDefault();
                let target = document.querySelector(link.getAttribute('href'));
                if (target) {
                    target.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                    });
                }
            });
        }
    }
}

const setCookie = (name, value, days) => {
    let date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    let expires = days ? "expires=" + date.toUTCString() : "";
    document.cookie = name + "=" + (value || "") + ";" + expires + ";path=/";
}

const getCookie = (cname) => {
    let name = cname + "=";
    let ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) == ' ') {
            c = c.substring(1);
        }
        if (c.indexOf(name) == 0) {
            return c.substring(name.length, c.length);
        }
    }
    return "";
}

const showPassword = (e) => {
    let button = document.querySelector('.show-password');
    if(!button) return;
    button.addEventListener('click', e => {
        e.preventDefault();
        let wrapper =  button.closest('.form__field-wrapper');
        if(!wrapper) return;
        let passwordField = wrapper.querySelector('.form__field');
        if (passwordField.type === 'password') {
            passwordField.type = 'text';
            wrapper.classList.add('show-password');
        } else {
            passwordField.type = 'password';
            if(wrapper.classList.contains('show-password')){
                wrapper.classList.remove('show-password');
            }
        }
    });
}
/**
 * Sets a cookie with the specified name and value.
 * // Example Usage:
 * // setCookie('myCookie', 'myValue', { expires: 3600, path: '/' });
 * @param {string} name - The name of the cookie.
 * @param {string} value - The value to set for the cookie.
 * @param {Object} [options] - Additional options for the cookie (optional).
 * @param {number|Date} [options.expires] - The expiration date of the cookie. Can be a number (in seconds) or a Date object.
 * @param {string} [options.path] - The path for which the cookie is valid. Defaults to '/'.
 * @param {string} [options.domain] - The domain for which the cookie is valid. Defaults to the current domain.
 * @param {boolean} [options.secure] - Indicates if the cookie should only be sent over secure (HTTPS) connections.
 * @param {boolean} [options.sameSite] - Controls whether the cookie is sent with cross-origin requests. Possible values: 'Strict', 'Lax', or 'None'.
 */
const setCookieHelper = (name, value, options = {}) => {
    let expires = options.expires;

    if (typeof expires === 'number' && expires) {
        const d = new Date();
        d.setTime(d.getTime() + expires * 1000); // Convert seconds to milliseconds
        expires = options.expires = d;
    }

    if (expires && expires.toUTCString) {
        options.expires = expires.toUTCString();
    }

    let cookie = encodeURIComponent(name) + '=' + encodeURIComponent(value);

    for (const key in options) {
        if (options.hasOwnProperty(key)) {
            cookie += '; ' + key;
            const value = options[key];
            if (value !== true) {
                cookie += '=' + value;
            }
        }
    }

    document.cookie = cookie;
}

/**
 * Retrieves the value of a cookie by its name.
 * // Example Usage:
 * // const myCookieValue = getCookie('myCookie');
 * // console.log(myCookieValue);
 * @param {string} name - The name of the cookie to retrieve.
 * @returns {string|null} The value of the cookie, or null if the cookie is not found.
 */
function getCookieHelper(name) {
    const decodedName = encodeURIComponent(name) + '=';
    const cookiesArray = document.cookie.split(';');

    for (let i = 0; i < cookiesArray.length; i++) {
        let cookie = cookiesArray[i].trim();

        if (cookie.indexOf(decodedName) === 0) {
            return decodeURIComponent(cookie.substring(decodedName.length, cookie.length));
        }
    }

    return null;
}

